import { Dayjs } from 'dayjs';

import { MapUtils } from '../../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { TimeZone } from '../../../time/timezone.js';

import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { cloneData, isSameData } from '../../dto/data-model-utils.js';

import { BusinessModuleRef } from '../business-module-ref.js';
import { VersionRange } from '../../core/version-range.js';
import { TrueTime } from '../../../time/true-time.js';

export enum AdStatus { Active = 'Active', Inactive = 'Inactive' }

export class Ad extends DataModel {
  static readonly TYPE_ID: string = '34f40333bc7646cdb77a946911d25a2b';
  static readonly SCHEMA_VERSION: number = 3;

  private constructor(
    public readonly adId: string,
    public readonly adName: string,
    public readonly adSetId: string,
    public readonly adSetName: string,
    public readonly campaignId: string,
    public readonly campaignName: string,
    public readonly adAccountId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly eventId: string,
    public readonly eventName: string,
    public readonly creationDate: Dayjs,
    public readonly startDate: Dayjs,
    public readonly endDate: Dayjs,
    public readonly status: AdStatus,
    public readonly budget: number,
    public readonly budgetSpent: number,
    public readonly impressions: number,
    public readonly reach: number,
    public readonly conversions: number,
    public readonly clicks: number,
    public readonly roas: number,
    public readonly costPerResult: number
  ) {
    super();
  }

  get $typeId(): string {
    return Ad.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(Ad.SCHEMA_VERSION);
  }

  static create(args: {
    adId: string,
    adName: string,
    adSetId: string,
    adSetName: string,
    campaignId: string,
    campaignName: string,
    adAccountId: string,
    businessRef: BusinessModuleRef,
    eventId: string,
    eventName: string,
    creationDate: Dayjs,
    startDate: Dayjs,
    endDate: Dayjs,
    status: AdStatus,
    budget: number,
    budgetSpent?: number,
    impressions?: number,
    reach?: number,
    conversions?: number,
    clicks?: number,
    roas?: number,
    costPerResult?: number
  }): Ad {
    return new Ad(
        args.adId,
        args.adName,
        args.adSetId,
        args.adSetName,
        args.campaignId,
        args.campaignName,
        args.adAccountId,
        args.businessRef,
        args.eventId,
        args.eventName,
        args.creationDate,
        args.startDate,
        args.endDate,
        args.status,
        args.budget,
        args.budgetSpent ?? 0,
        args.impressions ?? 0,
        args.reach ?? 0,
        args.conversions ?? 0,
        args.clicks ?? 0,
        args.roas ?? 0,
        args.costPerResult ?? 0
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): Ad {
    return Ad.create({
      adId: MapUtils.getString(map, 'adId'),
      adName: MapUtils.getString(map, 'adName'),
      adSetId: MapUtils.getString(map, 'adSetId'),
      adSetName: MapUtils.getString(map, 'adSetName'),
      campaignId: MapUtils.getString(map, 'campaignId'),
      campaignName: MapUtils.getString(map, 'campaignName'),
      adAccountId: MapUtils.getString(map, 'adAccountId'),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      eventId: MapUtils.getString(map, 'eventId'),
      eventName: MapUtils.getString(map, 'eventName'),
      creationDate: context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
      startDate: context.decodeDate(MapUtils.getInteger(map, 'startDate')),
      endDate: context.decodeDate(MapUtils.getInteger(map, 'endDate')),
      status: toEnumFromString(MapUtils.getString(map, 'status'), AdStatus),
      budget: MapUtils.getDouble(map, 'budget'),
      budgetSpent: MapUtils.getDouble(map, 'budgetSpent'),
      impressions: MapUtils.getInteger(map, 'impressions'),
      reach: MapUtils.getInteger(map, 'reach'),
      conversions: MapUtils.getInteger(map, 'conversions'),
      clicks: MapUtils.getInteger(map, 'clicks'),
      roas: MapUtils.getDouble(map, 'roas'),
      costPerResult: MapUtils.getDouble(map, 'costPerResult'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'adId': this.adId,
      'adName': this.adName,
      'adSetId': this.adSetId,
      'adSetName': this.adSetName,
      'campaignId': this.campaignId,
      'campaignName': this.campaignName,
      'adAccountId': this.adAccountId,
      'businessRef': this.businessRef.encode(),
      'eventId': this.eventId,
      'eventName': this.eventName,
      'creationDate': context.encodeDate(this.creationDate),
      'startDate': context.encodeDate(this.startDate),
      'endDate': context.encodeDate(this.endDate),
      'status': fromEnumAsString<AdStatus>(this.status),
      'budget': this.budget,
      'budgetSpent': this.budgetSpent,
      'impressions': this.impressions,
      'reach': this.reach,
      'conversions': this.conversions,
      'clicks': this.clicks,
      'roas': this.roas,
      'costPerResult': this.costPerResult,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    adId = this.adId,
    adName = this.adName,
    adSetId = this.adSetId,
    adSetName = this.adSetName,
    campaignId = this.campaignId,
    campaignName = this.campaignName,
    adAccountId = this.adAccountId,
    businessRef = cloneData(this.businessRef),
    eventId = this.eventId,
    eventName = this.eventName,
    creationDate = TimeZone.cloneDate(this.creationDate),
    startDate = TimeZone.cloneDate(this.startDate),
    endDate = TimeZone.cloneDate(this.endDate),
    status = this.status,
    budget = this.budget,
    budgetSpent = this.budgetSpent,
    impressions = this.impressions,
    reach = this.reach,
    conversions = this.conversions,
    clicks = this.clicks,
    roas = this.roas,
    costPerResult = this.costPerResult,
  }: {
    adId?: string,
    adName?: string,
    adSetId?: string,
    adSetName?: string,
    campaignId?: string,
    campaignName?: string,
    adAccountId?: string,
    businessRef?: BusinessModuleRef,
    eventId?: string,
    eventName?: string,
    creationDate?: Dayjs,
    startDate?: Dayjs,
    endDate?: Dayjs,
    status?: AdStatus,
    budget?: number,
    budgetSpent?: number,
    impressions?: number,
    reach?: number,
    conversions?: number,
    clicks?: number,
    roas?: number,
    costPerResult?: number
  } = {}): this {
    return Ad.create({
      adId,
      adName,
      adSetId,
      adSetName,
      campaignId,
      campaignName,
      adAccountId,
      businessRef,
      eventId,
      eventName,
      creationDate,
      startDate,
      endDate,
      status,
      budget,
      budgetSpent,
      impressions,
      reach,
      conversions,
      clicks,
      roas,
      costPerResult,
    }) as this;
  }

  isSame(other: Ad): boolean {
    if(other === this) {
      return true;
    }

    return other.adId === this.adId &&
      other.adName === this.adName &&
      other.adSetId === this.adSetId &&
      other.adSetName === this.adSetName &&
      other.campaignId === this.campaignId &&
      other.campaignName === this.campaignName &&
      other.adAccountId === this.adAccountId &&
      isSameData(other.businessRef, this.businessRef) &&
      other.eventId === this.eventId &&
      other.eventName === this.eventName &&
      TimeZone.isSameDay(other.creationDate, this.creationDate) &&
      TimeZone.isSameDay(other.startDate, this.startDate) &&
      TimeZone.isSameDay(other.endDate, this.endDate) &&
      other.status === this.status &&
      other.budget === this.budget &&
      other.budgetSpent === this.budgetSpent &&
      other.impressions === this.impressions &&
      other.reach === this.reach &&
      other.conversions === this.conversions &&
      other.clicks === this.clicks &&
      other.roas === this.roas &&
      other.costPerResult === this.costPerResult;
  }
}

export class AdBuilder extends DataModelBuilder<Ad> {
  build(context: SerializationContext, map: SerializedData): Ad {
    return Ad.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(Ad.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(Ad.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version: number = versionRange.to;

    // Added new field creationDate
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'creationDate', TrueTime.now());

      version = 2;
    }

    // Added new field adAccountId
    if(version < 3) {
      MapUtils.setIfNullOrUndefined(map, 'adAccountId', 'ZzyEI2MyQqtu2lBdKPBp');

      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
